/**
 * AgentCard - Premium card design matching v0 discover screen
 */

import { useCallback } from 'react';
import type { Agent } from '../types/agent';
import { hapticFeedback } from '../lib/telegram';
import { getCategoryStyle, getCategoryLabel } from '../lib/categories';

interface AgentCardProps {
  agent: Agent;
  onSelect: (agentId: string) => void;
  onVote?: (agentId: string) => void;
}

export function AgentCard({ agent, onSelect, onVote }: AgentCardProps) {
  const handleTap = useCallback(() => {
    hapticFeedback('light');
    onSelect(agent.id);
  }, [agent.id, onSelect]);

  const handleVote = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      hapticFeedback('medium');
      onVote?.(agent.id);
    },
    [agent.id, onVote]
  );

  const cat = getCategoryStyle(agent.category);
  const catLabel = getCategoryLabel(agent.category);
  void cat; // gradient no longer used in card bg

  return (
    <div
      onClick={handleTap}
      className="bg-[#242424] border border-white/5 rounded-xl p-4 shadow-lg
        cursor-pointer transition-all duration-200
        hover:shadow-xl hover:border-white/10 active:scale-[0.98]
        animate-fade-in-up
        focus-visible:ring-2 focus-visible:ring-[#0098EA] outline-none"
      tabIndex={0}
    >
      <div className="flex gap-3">
        {/* Logo */}
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#0098EA]/10 flex items-center justify-center overflow-hidden">
          {agent.logo ? (
            <img
              src={agent.logo}
              alt={agent.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = '<span class="text-xl">🤖</span>';
              }}
            />
          ) : (
            <span className="text-xl">🤖</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white truncate">{agent.name}</h3>
            {agent.verification_status === 'verified' && (
              <svg className="w-4 h-4 text-[#0098EA] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
              </svg>
            )}
            {agent.verification_status === 'featured' && (
              <span className="flex-shrink-0 text-yellow-400 text-sm">⭐</span>
            )}
            {agent.has_mini_app && (
              <span className="flex-shrink-0 text-xs">📱</span>
            )}
          </div>
          <p className="text-sm text-[#a0a0a0] line-clamp-2 leading-relaxed mb-2">
            {agent.description}
          </p>
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0098EA]/10 text-[#0098EA] border border-[#0098EA]/20">
              {catLabel}
            </span>
            <button
              onClick={handleVote}
              className={`
                flex items-center gap-1 h-8 px-3 rounded-lg text-sm transition-colors
                focus-visible:ring-2 focus-visible:ring-[#0098EA] outline-none
                ${agent.hasVoted
                  ? 'text-[#0098EA] hover:bg-white/5'
                  : 'text-white hover:bg-white/5'
                }
              `}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
              <span className="text-sm font-medium">{agent.vote_count}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
